import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, HelpCircle } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { TrendingStories } from "@/components/TrendingStories";
import { NewsFeed, FeedMeta } from "@/components/NewsFeed";
import { XFeed } from "@/components/XFeed";
import { Section } from "@/components/Section";
import { KeyNumbers, ParentChecklist, Glossary, GuideHub } from "@/components/HomeGuide";
import { TrendingSources } from "@/components/TrendingSources";
import { ResourceGroups } from "@/pages/Resources";
import { useNews } from "@/hooks/useNews";
import { useJsonLd } from "@/lib/site";
import { KNOWN, UNKNOWN, RESOURCES, EXPLAINER } from "@/data/content";

const HERO_IMG = "https://images.unsplash.com/photo-1541185933-ef5d8ed016c2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

const Hero = ({ lead }) => (
  <section data-testid="hero" className="relative grain overflow-hidden rounded-2xl border border-slate-800">
    <img src={HERO_IMG} alt="Rocket launch at night" className="absolute inset-0 h-full w-full object-cover" fetchPriority="high" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E14] via-[#0B0E14]/90 to-[#0B0E14]/30" />
    <div className="relative max-w-2xl p-6 sm:p-10 lg:p-14 space-y-5">
      <p className="rise font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-sky-400 flex items-center gap-2">
        <span className="live-dot h-1.5 w-1.5 rounded-full bg-red-500" /> Developing story
      </p>
      <h1 data-testid="hero-headline" className="rise font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]" style={{ animationDelay: "80ms" }}>
        SpaceX Shares for Every American Child? Latest News & Updates
      </h1>
      <p className="rise text-sm sm:text-base leading-relaxed text-slate-300" style={{ animationDelay: "160ms" }}>
        Elon Musk has reportedly announced plans to give one SpaceX share to every child in America in 2027. Details
        — including eligibility, timing and how shares would be held — are still developing. We track coverage from
        across the web automatically so you can follow the story in one place.
      </p>
      {lead && (
        <Link to={`/news/${lead.slug}`} data-testid="hero-top-story"
          className="rise block rounded-lg border border-slate-700/70 bg-[#121722]/80 backdrop-blur-md p-4 hover:border-[#0052FF] transition-colors" style={{ animationDelay: "240ms" }}>
          <span className="font-mono text-[10px] uppercase tracking-widest text-sky-400">Top story · {lead.source}</span>
          <span className="mt-1 block font-display font-semibold text-white">{lead.title}</span>
        </Link>
      )}
      <Link to="/latest-news" data-testid="hero-cta-latest" className="rise inline-flex items-center gap-2 rounded-full bg-[#0052FF] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1a63ff] transition-colors" style={{ animationDelay: "300ms" }}>
        All latest news <ArrowRight className="h-4 w-4" />
      </Link>
      <Link to="/news" data-testid="hero-browse-all" className="rise ml-3 inline-flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-sm font-semibold text-slate-200 hover:border-[#0052FF] hover:text-white transition-colors" style={{ animationDelay: "340ms" }}>
        Browse all stories by date
      </Link>
    </div>
  </section>
);

const KnowList = ({ title, items, icon: Icon, color, id }) => (
  <div data-testid={id} className="rounded-xl border border-slate-800 bg-[#121722] p-6 space-y-4">
    <h3 className="font-display font-semibold text-slate-100">{title}</h3>
    <ul className="space-y-3">
      {items.map((t) => (
        <li key={t} className="flex gap-3 text-sm leading-relaxed text-slate-300"><Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />{t}</li>
      ))}
    </ul>
  </div>
);

const StockPanel = lazy(() => import("@/components/StockPanel").then((m) => ({ default: m.StockPanel })));

export default function Home() {
  const news = useNews(13);
  const [lead, ...rest] = news.items;
  useJsonLd("ld-home-news", news.items.length ? {
    "@context": "https://schema.org", "@type": "ItemList", name: "Latest SpaceX child shares news",
    itemListElement: news.items.map((n, i) => ({ "@type": "ListItem", position: i + 1, url: `${window.location.origin}/news/${n.slug}`, name: n.title })),
  } : null);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-6 space-y-12">
      <Hero lead={lead} />
      <AdSlot id="home-hero" variant="hero" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-14">
          <Section id="latest-news" eyebrow="Automated feed" title="Latest News"
            action={<FeedMeta total={news.total} lastUpdated={news.last_updated} onRefresh={news.reload} />}>
            <NewsFeed items={rest.slice(0, 12)} loading={news.loading} error={news.error && !lead} adPrefix="home-infeed" />
            <Link to="/latest-news" data-testid="home-more-news-link" className="inline-flex items-center gap-2 text-sm font-semibold text-sky-400 hover:text-sky-300">
              See all news <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/news" data-testid="home-browse-all-link" className="ml-6 inline-flex items-center gap-2 text-sm font-semibold text-sky-400 hover:text-sky-300">
              Browse all stories by date <ArrowRight className="h-4 w-4" />
            </Link>
          </Section>
          <Section id="spacex-stock" eyebrow="Market data" title="SpaceX Stock (SPCX) Price & History"
            action={<Link to="/spacex-stock-price" data-testid="home-stock-page-link" className="text-sm text-sky-400 hover:text-sky-300">Share value calculator →</Link>}>
            <Suspense fallback={<div className="h-[420px] rounded-xl border border-slate-800 bg-[#121722]" />}><StockPanel /></Suspense>
          </Section>
          <AdSlot id="home-mid-1" variant="leaderboard" />
          <Section id="latest-from-x" eyebrow="Social" title="Latest From X"><XFeed /></Section>
          <AdSlot id="home-mid-2" variant="leaderboard" />
          <Section id="what-we-know" eyebrow="Fact check" title="What We Know So Far">
            <div className="grid gap-5 md:grid-cols-2">
              <KnowList id="known-list" title="Reported so far" items={KNOWN} icon={CheckCircle2} color="text-emerald-400" />
              <KnowList id="unknown-list" title="Outstanding questions" items={UNKNOWN} icon={HelpCircle} color="text-amber-400" />
            </div>
            <p className="text-xs text-slate-500">We do not publish unverified eligibility requirements or claim instructions. There is currently no official way to apply.</p>
          </Section>
          <KeyNumbers />
          <Section id="explainer" eyebrow="Explainer" title="The SpaceX Share for Every Child Plan, Explained">
            <div className="grid gap-5 md:grid-cols-2">
              {EXPLAINER.map((e) => (
                <article key={e.h} className="rounded-xl border border-slate-800 bg-[#181F2C] p-5 space-y-2">
                  <h3 className="font-display font-semibold text-slate-100">{e.h}</h3>
                  <p className="text-sm leading-relaxed text-slate-400">{e.p}</p>
                </article>
              ))}
            </div>
            <Link to="/faqs" data-testid="home-faqs-link" className="inline-flex items-center gap-2 text-sm font-semibold text-sky-400 hover:text-sky-300">
              Read all FAQs <ArrowRight className="h-4 w-4" />
            </Link>
          </Section>
          <ParentChecklist />
          <AdSlot id="home-mid-3" variant="leaderboard" />
          <Glossary />
          <GuideHub />
          <Section id="useful-resources" eyebrow="Learn" title="Useful Resources"
            action={<Link to="/resources" data-testid="home-resources-link" className="text-sm text-sky-400 hover:text-sky-300">All resources →</Link>}>
            <ResourceGroups groups={RESOURCES.slice(0, 2)} />
          </Section>
        </div>
        <aside className="lg:col-span-4 space-y-8">
          <TrendingSources />
          <AdSlot id="sidebar-1" variant="sidebar" />
          <TrendingStories />
          <div className="lg:sticky lg:top-24">
            <AdSlot id="sidebar-2" variant="skyscraper" className="hidden lg:block" />
          </div>
        </aside>
      </div>
    </div>
  );
}
